import {Injectable} from '@angular/core';
import {IOperative} from '../../message-models/operative.interface';
import {Observable, of} from 'rxjs';
import {CachedData} from '../../common/cached-data.class';
import {BaseApiService} from '../../common/base-api.service';
import {HttpClient} from '@angular/common/http';
import {SettingsProviderService} from '../../common/settings-provider.service';
import {IRiskInfo} from '../../message-models/risk-info.interface';
import {IJobLevelInfo} from '../../message-models/job-level.interface';
import {OperativeInfo} from '../../domain-model/operative-info.class';
import {map} from 'rxjs/operators';
import {IRiskMatrixEntry} from '../../message-models/risk-matrix-entry.interface';
import {ICompanyEngagementHistory} from "../../message-models/company-engagement-history.interface";
import {IEmploymentStatus} from '../../message-models/employment-status.interface';

@Injectable({
  providedIn: 'root'
})
export class OperativesService extends BaseApiService {

  operativeData: OperativeData = new OperativeData(
    [], '', 100000
  );

  constructor(http: HttpClient) {
    super(http, new SettingsProviderService('ecaApi'), 'Operatives');
  }

  getOperativeByName(operativeFullName: string) {
    return this.operativeData.data.find(d => d.getFullName() === operativeFullName);
  }

  getOperativeById(operativeId: string, alwaysServer: boolean = false) {
    let operative = !alwaysServer ? this.operativeData.data.find(d => d.id === operativeId) : null;

    if (operative)
      return of(operative);

    this.invalidateOperativeData();
    return this.post<OperativeInfo>('GetOperative', { operativeId: operativeId});
  }

  createOrUpdateOperative(operative: OperativeInfo) {
    return this.post<OperativeInfo>('CreateOrUpdateOperative', operative);
  }

  findOperativesWithLastNameStartingWith(companyId: string, lastNameStartsWith: string) : Observable<OperativeInfo[]> {
    return this.post<OperativeInfo[]>('FindOperativesWithLastNameStartingWith', { companyId, lastNameStartsWith });
  }


  createOrUpdateEngagement(engagement: ICompanyEngagementHistory) {
    return this.post<ICompanyEngagementHistory>('CreateOrUpdateCompanyEngagement', engagement);
  }

  removeOperativeFromCompany(operative: OperativeInfo, asOf: string) {
    return this.post<OperativeInfo>('RemoveOperativeFromCompany', { operativeId: operative.id, companyId : operative.companyId, asOf});
  }

  getOperativesInCompany(companyId: string, asOf: string, forceServer: boolean = false,allHistory: boolean = false): Observable<OperativeInfo[]> {
    if (!forceServer && this.operativeData && this.operativeData.companyId === companyId && this.operativeData.isDataFresh()) {
      return of(this.operativeData.data);
    } else {
      if (!asOf)
         asOf = null;
      return this.post<IOperative[]>('GetOperativesInCompany', {companyId: companyId, allHistory: allHistory, asOf: asOf})
        .pipe(
          map(operatives => {
            this.operativeData.clear();
            operatives.forEach(o => {
              this.operativeData.addData(new OperativeInfo(o));
            })
            this.operativeData.companyId = companyId;
            return this.operativeData.data;
          }));
    }
  }

  invalidateOperativeData() {
    if (!this.operativeData) {
      return;
    }

    this.operativeData.invalidateData();
  }

  setOperativeRiskAndLevels(risks: IRiskInfo[], jobLevels: IJobLevelInfo[], riskMatrix: IRiskMatrixEntry[], employmentStatuses: IEmploymentStatus[]) {
    if (!this.operativeData || !this.operativeData.data) {
      return;
    }

    this.setOperativeRisks(risks);
    this.setOperativeJobLevels(jobLevels);
    this.setOperativeRiskMatrixLevels(riskMatrix);
    this.setOperativeEmploymentStatus(employmentStatuses);

  }

  setOperativeRisks(risks: IRiskInfo[]) {
    if (!this.operativeData || !this.operativeData.data || !risks) {
      return;
    }

    this.operativeData.data.forEach(o => {
      o.populateRisk(risks);
    });
  }

  setOperativeJobLevels(jobLevels: IJobLevelInfo[]) {
    if (!this.operativeData || !this.operativeData.data || !jobLevels) {
      return;
    }

    this.operativeData.data.forEach(o => {
      o.populateJobLevel(jobLevels);
    });
  }

  setOperativeEmploymentStatus(employmentStatuses: IEmploymentStatus[]) {
    if (!this.operativeData || !this.operativeData.data || !employmentStatuses) {
      return;
    }
    this.operativeData.data.forEach(o => {
      o.populateEmploymentStatus(employmentStatuses);
    });
  }
  setOperativeRiskMatrixLevels(riskMatrix: IRiskMatrixEntry[]) {
    if (!this.operativeData || !this.operativeData.data || !riskMatrix) {
      return;
    }

    this.operativeData.data.forEach(o => {
      o.populateRiskMatrixLevel(riskMatrix);
    });
  }

  getCompanyEngagementsFor(operativeId: string, companyId: string): Observable<ICompanyEngagementHistory[]> {
    return this.post<ICompanyEngagementHistory[]>('GetCompanyEngagementsFor', {operativeId, companyId});
  }

  deleteCompanyEngagement(roleHistory: ICompanyEngagementHistory) {
    return this.post<ICompanyEngagementHistory[]>('DeleteCompanyEngagement', roleHistory);
  }
}

export class OperativeData extends CachedData<OperativeInfo> {

  companyId: string = '';

  constructor(operatives: OperativeInfo[], companyId, numSecondsToKeep: number = 300) {
    super(operatives, numSecondsToKeep);
  }


}
